import {
  ENTITY_TYPE_DOCUMENTATION,
  TUTORIAL_DOC_TYPE,
  LEGO_BRICK_DOC_TYPE,
  MIGRATION_DOC_TYPE,
  PLAYBOOK_DOC_TYPE,
  RECORD_DOC_TYPE,
} from '@internal/plugin-neo4j-techdocs-common';
import {
  RiBookOpenLine,
  RiExchangeLine,
  RiFileList3Line,
  RiGraduationCapLine,
  RiStackLine,
} from '@remixicon/react';
import type { RemixiconComponentType } from '@remixicon/react';

export const LENGTH_ANNOTATION = 'neo4j/duration';
export const DOCS_LAST_UPDATE_ANNOTATION = 'neo4j/last-docs-update';

export const RECENT_DOCS_TITLE = 'Recently viewed';

export const DOCS_SURFACE_OWNER_ENTITY_REF =
  'group:default/platform-surface-aura';

export type DocTypeData = {
  type: string;
  title: string;
  /** Singular form passed to pluralize, for example "lego brick". */
  noun: string;
  icon: RemixiconComponentType;
  summary: string;
  description: string;
  example: { title: string; summary: string };
  templateName: string;
  entityType: string;
};

/**
 * Category insertion order defines the order of the docs home cards and the
 * docs sidebar.
 */
export const DOC_TYPES: Record<string, DocTypeData> = {
  [LEGO_BRICK_DOC_TYPE]: {
    type: LEGO_BRICK_DOC_TYPE,
    title: 'Lego Bricks',
    noun: 'lego brick',
    icon: RiStackLine,
    summary: 'Step-by-step setup instructions.',
    description:
      "A clear, step-by-step instruction manual for setting up some capability, with details of the different features it offers and the advantages of using it. Readers shouldn't be expected to remember anything from the manual for later.",
    templateName: 'create-lego-brick',
    example: {
      title: 'How to add Doppler secrets to a component',
      summary:
        'Which could include the setup steps, and what the component gets from Doppler.',
    },
    entityType: ENTITY_TYPE_DOCUMENTATION,
  },
  [MIGRATION_DOC_TYPE]: {
    type: MIGRATION_DOC_TYPE,
    title: 'Migrations',
    noun: 'migration',
    icon: RiExchangeLine,
    summary: 'Move from one tool or version to another.',
    description:
      'An instruction manual to update a capability from one version/implementation to another.',
    templateName: 'create-migration',
    example: {
      title: 'How to migrate from Datadog to Grafana',
      summary:
        'Which could include how to move the dashboards and alerts of a component to Grafana.',
    },
    entityType: ENTITY_TYPE_DOCUMENTATION,
  },
  [PLAYBOOK_DOC_TYPE]: {
    type: PLAYBOOK_DOC_TYPE,
    title: 'Playbooks',
    noun: 'playbook',
    icon: RiBookOpenLine,
    summary: 'Diagnose and fix a live problem.',
    description:
      'A guide for diagnosing and resolving an ongoing issue with a component or capability.',
    templateName: 'create-playbook',
    example: {
      title: 'Diagnose ImagePullBackOff errors on a pod',
      summary:
        'Which could include how to find out why a pod cannot pull its container image, and how to fix it.',
    },
    entityType: ENTITY_TYPE_DOCUMENTATION,
  },
  [TUTORIAL_DOC_TYPE]: {
    type: TUTORIAL_DOC_TYPE,
    title: 'Tutorials',
    noun: 'tutorial',
    icon: RiGraduationCapLine,
    summary: 'Hands-on learning with outcomes.',
    description:
      "Documentation teaching someone how to use a capability, with a focus on hands-on experience. Readers should be able to take what they've learned and remember it for later.",
    templateName: 'create-tutorial',
    example: {
      title: 'Learn to use Kubernetes ConfigMaps, hands-on',
      summary:
        'Which could include a lab that teaches how to create a ConfigMap and use it in a pod.',
    },
    entityType: ENTITY_TYPE_DOCUMENTATION,
  },
  [RECORD_DOC_TYPE]: {
    type: RECORD_DOC_TYPE,
    title: 'Records',
    noun: 'record',
    icon: RiFileList3Line,
    summary: 'Why we made a technical decision.',
    description:
      'A historical record of why a decision was made, or why something is the way it is. Readers should be able to understand the context and reasoning behind the decision.',
    templateName: 'create-adr',
    example: {
      title: 'Why each component gets its own namespace',
      summary:
        'Which could include the reasons for the namespace isolation strategy, and the other options that the team looked at.',
    },
    entityType: ENTITY_TYPE_DOCUMENTATION,
  },
};

export const DOC_TYPE_LIST = Object.values(DOC_TYPES);
